import React, { useCallback, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import css from './abstractHoverZone.module.scss';
import { AbstractHoverZoneBaseProps } from './types';
import { Popover } from '@fullstory/components';

export interface Offset {
  top: number;
  left: number;
  right: number;
  bottom: number;
}

const getOffset = (element: HTMLElement): Offset => {
  const { top, left, width, height } = element.getBoundingClientRect();

  return {
    top: top + window.scrollY,
    left: left + window.scrollX,
    right: left + window.scrollX + width,
    bottom: top + window.scrollY + height,
  };
};

export const AbstractHoverZoneBase = <D,>({
  overlay,
  children,
  positionKey,
  tooltipX,
  tooltipY,
  tooltipData,
  tooltipOffset,
  tooltipPlacement = 'top',
  onMouseMove,
  onMouseLeave,
  onMouseDown,
  onMouseUp,
}: AbstractHoverZoneBaseProps<D>) => {
  const ref = useRef<HTMLDivElement>(null);
  const [tooltipAnchor, setTooltipAnchor] = useState<HTMLDivElement | null>(null);

  const handleMouseLeave = useCallback(
    (e: React.MouseEvent) => {
      if (!ref.current) {
        onMouseLeave(e, null);
        return;
      }

      const elementOffset = getOffset(ref.current);

      const mouseX = window.scrollX + e.clientX;
      /**
       * sub pixels mess up the mouse out calculations on the ends (right, bottom)
       * of the container. We can floor them so they properly trigger when the mouse
       * leaves the zone.
       **/
      if (mouseX < elementOffset.left || mouseX >= Math.floor(elementOffset.right)) {
        onMouseLeave(e, elementOffset);
      }

      const mouseY = window.scrollY + e.clientY;
      if (mouseY < elementOffset.top || mouseY >= Math.floor(elementOffset.bottom)) {
        onMouseLeave(e, elementOffset);
      }
    },
    [onMouseLeave],
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!ref.current) {
        onMouseMove(e, null);
        return;
      }

      const elementOffset = getOffset(ref.current);
      onMouseMove(e, elementOffset);
    },
    [onMouseMove],
  );

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (!ref.current) {
        onMouseDown?.(e, null);
        return;
      }

      const elementOffset = getOffset(ref.current);
      onMouseDown?.(e, elementOffset);
    },
    [onMouseDown],
  );

  const handleMouseUp = useCallback(
    (e: React.MouseEvent) => {
      if (!ref.current) {
        onMouseUp?.(e, null);
        return;
      }

      const elementOffset = getOffset(ref.current);
      onMouseUp?.(e, elementOffset);
    },
    [onMouseUp],
  );

  return (
    overlay &&
    createPortal(
      <div
        ref={ref}
        className={css.hoverZone}
        onMouseLeave={handleMouseLeave}
        onMouseMove={handleMouseMove}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        role="presentation"
      >
        <div
          key={positionKey}
          className={css.transitionContainer}
          style={{ transform: `translate(${tooltipX}px, ${tooltipY}px)` }}
          ref={setTooltipAnchor}
        />
        {tooltipData && (
          <Popover
            anchor={tooltipAnchor}
            variant="dark"
            placement={tooltipPlacement}
            arrow
            open
            offset={tooltipOffset}
          >
            {children(tooltipData)}
          </Popover>
        )}
      </div>,
      overlay,
    )
  );
};
